import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Typography, CircularProgress } from "@mui/material";
import {
  fetchUserProfileFromFirestore,
  fetchCurrentUserProfile,
  fetchUserChallenges,
} from "../../services/profileService";
import { fetchHomePageChallenges } from "../../services/challengeService";
import { fetchChallengeSubmissions } from "../../services/submissionService";
import ProfileChallengesSection from "../organisms/Profile/ProfileChallengesSection";
import { useTheme } from "styled-components";
import HomePageUserHeader from "../atoms/HomePageUserHeader";
import HomePageHeader from "../atoms/HomePageHeader";
import HomePageChallengesSection from "../organisms/HomePage/HomePageChallengesSection";
import HomePageSubmissionsSection from "../organisms/HomePage/HomePageSubmissionsSection";

const HomePage = () => {
  const { profileId } = useParams();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [latestChallenges, setLatestChallenges] = useState([]);
  const [latestSubmissions, setLatestSubmissions] = useState([]); // New state for submissions
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchProfilesAndChallenges = async () => {
      setLoading(true);
      try {
        // Fetch the latest challenges
        const fetchedLatestChallenges = await fetchHomePageChallenges();
        setLatestChallenges(fetchedLatestChallenges.slice(0, 3));

        // Fetch the latest submissions
        const fetchedLatestSubmissions = await fetchChallengeSubmissions();
        setLatestSubmissions(fetchedLatestSubmissions.slice(0, 3));

        // Fetch the current user profile
        const loggedInUser = await fetchCurrentUserProfile();
        setCurrentUser(loggedInUser);

        // Check and update userProfile in localStorage
        const storedUserProfile = JSON.parse(
          localStorage.getItem("userProfile")
        );
        if (storedUserProfile) {
          // Check if uid is missing and update it
          if (!storedUserProfile.uid && loggedInUser?.uid) {
            storedUserProfile.uid = loggedInUser.uid;
            localStorage.setItem(
              "userProfile",
              JSON.stringify(storedUserProfile)
            );
          }
        }

        let userToFetch = profileId || loggedInUser?.uid;

        if (userToFetch) {
          const fetchedProfile = await fetchUserProfileFromFirestore(
            userToFetch
          );
          setUserProfile(fetchedProfile);

          // Fetch challenges created by this user
          const fetchedChallenges = await fetchUserChallenges(
            userToFetch,
            loggedInUser?.uid
          );

          setChallenges(fetchedChallenges.slice(0, 3));
        }
      } catch (err) {
        setError("Error fetching profile or challenges data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfilesAndChallenges();
  }, [profileId, navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Extract the first name if the user profile is available
  const cleanedDisplayName = (userProfile?.displayName || "User")
    .trim()
    .replace(/\s+/g, " ");
  const firstName = cleanedDisplayName.split(" ")[0];

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box>
        <Box>
          {!currentUser ? (
            <HomePageHeader />
          ) : (
            <>
              <HomePageUserHeader currentUser={currentUser} />

              {/* Pass profileId or currentUser's ID to the ProfileChallengesSection */}
              <ProfileChallengesSection
                firstName={firstName}
                challenges={challenges}
                profileId={currentUser?.uid} // Pass the userId here
                theme={theme}
              />
            </>
          )}

          {/* Conditionally render the HomePageChallengesSection if there are challenges */}
          {latestChallenges.length > 0 && (
            <HomePageChallengesSection
              header="Latest Challenges"
              challenges={latestChallenges}
              link={`/challenge/all`}
              theme={theme}
            />
          )}

          {/* Conditionally render the HomePageSubmissionsSection if there are submissions */}
          {latestSubmissions.length > 0 && (
            <HomePageSubmissionsSection
              header="Latest Submissions"
              submissions={latestSubmissions}
              link={`/challenge/submissions/all`}
              theme={theme}
            />
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default HomePage;
